import type { PropertyPublic, WorldManifest } from "@townsy/contracts";
import type { MinimapCar, MinimapSnapshot } from "../../app/townSession";
import {
  YOU_ARE_HERE,
  minimapPlotState,
  rectToMinimap,
  worldToMinimap,
  yawToCanvasAngle,
  type DistrictLabel,
  type DistrictRoad,
  type MinimapPlot,
  type MinimapView,
  type RoadLink,
  type StreetLabel,
} from "./minimapModel";

/**
 * Canvas drawing of the town map (browser only), shared by the Minimap component and the renderer
 * harness's map view (world v4 evidence), so both draw exactly the same picture.
 */
export const MINIMAP_COLORS = {
  grass: "#dfe9cf",
  plaza: "#eadfca",
  sidewalk: "#f3eee4",
  road: "#8d939e",
  owned: "#4a64d6",
  available: "#ffffff",
  availableEdge: "#c98a1b",
  civic: "#2f8a5b",
  selected: "#1b1f2a",
  label: "#1b1f2a",
  halo: "rgba(255,255,255,0.92)",
  car: "#1f232b",
  carDriven: "#8a4b00",
  myCar: "#f0a02a",
  me: "#d63c2f",
  meEdge: "#ffffff",
  /** ADR-0042: the name on a road to another district (Here view); world v5: district outlines (Town view). */
  link: "#0f6f66",
  district: "rgba(27,31,42,0.45)",
  here: "#1b1f2a",
} as const;

const C = MINIMAP_COLORS;

export const minimapLabelFont = (px: number) => `600 ${px}px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;

function fillRect(g: CanvasRenderingContext2D, v: MinimapView, r: { minX: number; minZ: number; maxX: number; maxZ: number }, color: string): void {
  const p = rectToMinimap(v, r);
  g.fillStyle = color;
  g.fillRect(p.x, p.y, Math.max(p.w, 1), Math.max(p.h, 1));
}

/** Streets, plots (owned / for sale / Townsy) and street names: the layer drawn once. */
export function drawMinimapStatic(
  g: CanvasRenderingContext2D,
  v: MinimapView,
  manifest: WorldManifest,
  plots: readonly MinimapPlot[],
  properties: Record<string, PropertyPublic>,
  selected: string | null,
  labels: readonly StreetLabel[],
  fontPx: number,
): void {
  g.fillStyle = C.grass;
  g.fillRect(0, 0, v.width, v.height);
  for (const s of manifest.surfaces) if (s.kind === "plaza") fillRect(g, v, s.aabb, C.plaza);
  for (const s of manifest.surfaces) if (s.kind === "sidewalk") fillRect(g, v, s.aabb, C.sidewalk);
  for (const s of manifest.surfaces) if (s.kind === "road" || s.kind === "crosswalk") fillRect(g, v, s.aabb, C.road);
  for (const p of plots) {
    const state = minimapPlotState(p.type, properties[p.id]);
    const r = rectToMinimap(v, p.bounds);
    const inset = Math.min(1, r.w / 4, r.h / 4);
    const x = r.x + inset;
    const y = r.y + inset;
    const w = Math.max(r.w - inset * 2, 1.5);
    const h = Math.max(r.h - inset * 2, 1.5);
    if (state === "available") {
      g.fillStyle = C.available;
      g.fillRect(x, y, w, h);
      g.strokeStyle = C.availableEdge;
      g.lineWidth = 1;
      g.strokeRect(x + 0.5, y + 0.5, Math.max(w - 1, 0.5), Math.max(h - 1, 0.5));
    } else {
      g.fillStyle = state === "civic" ? C.civic : C.owned;
      g.fillRect(x, y, w, h);
    }
    if (p.id === selected) {
      g.strokeStyle = C.selected;
      g.lineWidth = 2;
      g.strokeRect(r.x - 1, r.y - 1, r.w + 2, r.h + 2);
    }
  }
  g.font = minimapLabelFont(fontPx);
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.lineJoin = "round";
  for (const l of labels) {
    g.save();
    g.translate(l.x, l.y);
    g.rotate(l.angle);
    g.strokeStyle = C.halo;
    g.lineWidth = 3;
    g.strokeText(l.name, 0, 0.5);
    g.fillStyle = C.label;
    g.fillText(l.name, 0, 0.5);
    g.restore();
  }
}

function haloText(g: CanvasRenderingContext2D, text: string, x: number, y: number, color: string = C.label): void {
  g.strokeStyle = C.halo;
  g.lineWidth = 3;
  g.strokeText(text, x, y);
  g.fillStyle = color;
  g.fillText(text, x, y);
}

/**
 * ADR-0042, Here view: each road to another district, drawn as road from the district's streets to the map's edge (it
 * runs on off the map), with "To Riverside" over it in the link colour.
 */
export function drawRoadLinks(g: CanvasRenderingContext2D, v: MinimapView, links: readonly RoadLink[], fontPx: number): void {
  g.save();
  g.fillStyle = C.road;
  for (const l of links) {
    for (const r of l.roads) {
      const p = rectToMinimap(v, r);
      g.fillRect(p.x, p.y, Math.max(p.w, 1), Math.max(p.h, 1));
    }
    g.fillRect(l.stub.x, l.stub.y, l.stub.w, l.stub.h);
  }
  g.font = minimapLabelFont(fontPx);
  g.textBaseline = "middle";
  g.lineJoin = "round";
  for (const l of links) {
    g.textAlign = l.label.align;
    haloText(g, l.label.text, l.label.x, l.label.y + 0.5, C.link);
  }
  g.restore();
}

/**
 * World v5, Town view: every district's outline (the visitor's thicker) and name, "you are here" under the visitor's.
 * ADR-0042: the roads between districts are drawn over the outlines (at least 3 px thick), so each district line is
 * visibly open where the road crosses it.
 */
export function drawDistrictLabels(g: CanvasRenderingContext2D, labels: readonly DistrictLabel[], fontPx: number, v?: MinimapView, roads: readonly DistrictRoad[] = []): void {
  g.save();
  for (const l of labels) {
    g.strokeStyle = l.here ? C.here : C.district;
    g.lineWidth = l.here ? 2 : 1;
    g.strokeRect(l.box.x + 0.5, l.box.y + 0.5, Math.max(l.box.w - 1, 1), Math.max(l.box.h - 1, 1));
  }
  if (v) {
    g.fillStyle = C.road;
    for (const road of roads) {
      for (const r of road.roads) {
        const p = rectToMinimap(v, r);
        // Thin roads (the whole town at a quarter pixel per metre) keep 3 px across the line.
        if (road.axis === "x") {
          const h = Math.max(p.h, 3);
          g.fillRect(p.x, p.y + p.h / 2 - h / 2, Math.max(p.w, 3), h);
        } else {
          const w = Math.max(p.w, 3);
          g.fillRect(p.x + p.w / 2 - w / 2, p.y, w, Math.max(p.h, 3));
        }
      }
    }
  }
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.lineJoin = "round";
  for (const l of labels) {
    g.font = minimapLabelFont(fontPx + 1);
    haloText(g, l.name, l.x, l.y + 0.5);
    if (l.here) {
      g.font = minimapLabelFont(fontPx - 1);
      haloText(g, YOU_ARE_HERE, l.x, l.y + fontPx + 1.5, C.me);
    }
  }
  g.restore();
}

function drawCar(g: CanvasRenderingContext2D, v: MinimapView, car: MinimapCar, color: string, minLength: number): void {
  const c = worldToMinimap(v, car.x, car.z);
  const len = Math.max(car.halfLength * 2 * v.scale, minLength);
  const wid = Math.max(car.halfWidth * 2 * v.scale, minLength * 0.55);
  g.save();
  g.translate(c.x, c.y);
  g.rotate(yawToCanvasAngle(car.heading));
  g.fillStyle = color;
  g.fillRect(-wid / 2, -len / 2, wid, len);
  g.restore();
}

/** The moving things: cars, the car you drive, you and your heading. */
export function drawMinimapDynamic(g: CanvasRenderingContext2D, v: MinimapView, snap: MinimapSnapshot): void {
  for (const car of snap.cars) drawCar(g, v, car, car.driven ? C.carDriven : C.car, 4);
  if (snap.driving) {
    drawCar(g, v, snap.driving, C.myCar, 6);
    const c = worldToMinimap(v, snap.driving.x, snap.driving.z);
    g.strokeStyle = C.selected;
    g.lineWidth = 1;
    g.beginPath();
    g.arc(c.x, c.y, 6, 0, Math.PI * 2);
    g.stroke();
  }
  if (snap.local) {
    const c = worldToMinimap(v, snap.local.x, snap.local.z);
    g.save();
    g.translate(c.x, c.y);
    g.rotate(yawToCanvasAngle(snap.local.heading));
    g.beginPath();
    g.moveTo(0, -7);
    g.lineTo(5, 5);
    g.lineTo(0, 2.5);
    g.lineTo(-5, 5);
    g.closePath();
    g.fillStyle = C.me;
    g.strokeStyle = C.meEdge;
    g.lineWidth = 1.5;
    g.stroke();
    g.fill();
    g.restore();
  }
}
